import React, { ReactElement } from 'react';

// Hooks
import {
    useActiveTiptapEditorTouchCallback,
    useTiptapActiveEditorSelector,
} from '../../../../../components/tiptapEditor/store/tiptapEditorStoreHooks';

// Utils
import {
    isListTypeClosestActive,
    hasNestedListContent,
} from '../../../../../../common/tiptap/extensions/list/tiptapListUtils';
import { Editor, findParentNode } from '@tiptap/react';

// Components
import RichTextTool from './RichTextTool';

// Types
import { ToolbarToolProps } from '../../../config/toolDefinitions/toolbarToolTypes';
import { TiptapNodeType } from '../../../../../../common/tiptap/tiptapTypes';

type IndentCommand = 'sinkListItem' | 'liftListItem';

type TiptapIndentControlProps = ToolbarToolProps & {
    command: IndentCommand;
    listTypeNames?: TiptapNodeType[];
    listItemTypeName?: TiptapNodeType;
    canExecuteCommand?: (editor: Editor) => boolean;
};

const DEFAULT_LIST_TYPE_NAMES = [TiptapNodeType.orderedList, TiptapNodeType.bulletList];

const runIndentCommand = (editor: Editor, command: IndentCommand, listItemTypeName: TiptapNodeType) => {
    if (command === 'sinkListItem') return editor.commands?.[command]?.(listItemTypeName);

    // liftListItemWithNestedContent only handles nested lists; fall back to the standard command otherwise.
    const listItem = findParentNode((node) => node.type.name === listItemTypeName)(editor.state.selection);

    return listItemTypeName === TiptapNodeType.listItem && listItem && hasNestedListContent(listItem.node)
        ? editor.commands.liftListItemWithNestedContent()
        : editor.commands?.[command]?.(listItemTypeName);
};

const TiptapTextIndentControlTool = ({
    command,
    listTypeNames = DEFAULT_LIST_TYPE_NAMES,
    listItemTypeName = TiptapNodeType.listItem,
    canExecuteCommand,
    ...props
}: TiptapIndentControlProps): ReactElement => {
    const canExecute = useTiptapActiveEditorSelector((editor) => {
        const isListActive = listTypeNames.some((listTypeName) => isListTypeClosestActive(listTypeName)(editor));

        if (!isListActive || !editor) return false;

        return canExecuteCommand?.(editor) ?? editor.can()?.[command]?.(listItemTypeName) ?? false;
    });

    const handleClick = useActiveTiptapEditorTouchCallback(
        (editor, event) => {
            event?.preventDefault();
            runIndentCommand(editor, command, listItemTypeName);
        },
        [command, listItemTypeName],
    );

    return (
        <RichTextTool
            onClick={handleClick}
            iconName={props.iconName || 'toolbar-indent'}
            disabled={!canExecute}
            {...props}
        />
    );
};

export default TiptapTextIndentControlTool;
